/* eslint-disable no-empty */
// utils
import { motion } from 'framer-motion';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IoCloseSharp, IoFilterOutline } from 'react-icons/io5';
import { useAuth } from '../../components/context/Info';
import WhatsAppComponent from '../../components/fixed/whatsapp/WhatsappComponent';
import PopUpComponent from '../../components/popUp/PopUpComponent';
import { getDefaultServerSideProps } from '../../lib/server/site-props';
import { getCompany, getCompanySearch } from '../../services/selectors';
import { formatSelects, useWindowSize } from '../../utils/FunctionsGlobals';
import { appendCodsiteQuery, buildCanonical } from '../../utils/url';
import WelcomePiecesProperties2 from '../imoveis/components/pieces/properties/welcome2/WelcomePiecesProperties';
import styles from './Company.module.css';
import ListPiecesCondominio from './components/pieces/list/ListPiecesCondominio';

const AboutPieces = dynamic(() => import('../../components/pieces/about/AboutPieces'), { ssr: false });
const AboutPieces1 = dynamic(() => import('../../components/pieces/about1/AboutPieces1'), { ssr: false });
const AboutPieces2 = dynamic(() => import('../../components/pieces/about1/AboutPieces2'), { ssr: false });

const QTD_PAGINA = 12;

export const getServerSideProps = getDefaultServerSideProps;

type CondominioFilters = {
    nome: string;
    cidade: string;
    bairro: string;
    tipo: string;
    preco_min: string;
    preco_max: string;
};

const EMPTY_FILTERS: CondominioFilters = {
    nome: '',
    cidade: '',
    bairro: '',
    tipo: '',
    preco_min: '',
    preco_max: ''
};

const getQueryStringValue = (value: any) => {
    if (Array.isArray(value)) {
        return String(value[0] || '');
    }
    return String(value || '');
};

const normalizeFiltersFromQuery = (query: any): CondominioFilters => ({
    nome: getQueryStringValue(query?.nome),
    cidade: getQueryStringValue(query?.cidade),
    bairro: getQueryStringValue(query?.bairro),
    tipo: getQueryStringValue(query?.tipo),
    preco_min: getQueryStringValue(query?.preco_min).replace(/[^\d]/g, ''),
    preco_max: getQueryStringValue(query?.preco_max).replace(/[^\d]/g, '')
});

const hasAnyFilter = (filters: CondominioFilters) => {
    return Object.values(filters).some((value) => String(value || '').trim().length > 0);
};

const filtersToQuery = (filters: CondominioFilters) => {
    const query: Record<string, string> = {};

    if (filters.nome.trim()) query.nome = filters.nome.trim();
    if (filters.cidade.trim()) query.cidade = filters.cidade.trim();
    if (filters.bairro.trim()) query.bairro = filters.bairro.trim();
    if (filters.tipo.trim()) query.tipo = filters.tipo.trim();
    if (filters.preco_min.trim()) query.preco_min = filters.preco_min.trim();
    if (filters.preco_max.trim()) query.preco_max = filters.preco_max.trim();

    return query;
};

const toOptionalNumber = (value: string) => {
    const parsed = Number(value);
    if (!value || Number.isNaN(parsed) || parsed <= 0) {
        return undefined;
    }
    return parsed;
};

const formatFilterCurrency = (value: string) => {
    const amount = Number(value);
    if (!amount || Number.isNaN(amount)) {
        return value;
    }
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        maximumFractionDigits: 0
    }).format(amount);
};

const Company = () => {
    const router = useRouter();
    const size = useWindowSize();
    const isMobileFilter = size?.width ? size.width <= 900 : false;

    const {
        allDataHome,
        isMobile1,
        favorito,
        init_props
    } = useAuth();

    const allCity = allDataHome?.cidades;
    const allCities = allCity ? formatSelects(allCity) : [];
    const site = allDataHome?.site;
    const [activeLargeFilter, setActiveLargeFilter] = useState(false);
    const [codsite] = useState<number>(init_props?.codsite);
    const [origin] = useState<any>(init_props?.prodOrigin);
    const codsite2 = init_props?.codsite;

    const [allCondominios, setAllCondominios] = useState<any[]>([]);
    const [paginaAtual, setPaginaAtual] = useState<number>(0);
    const [totalCondominios, setTotalCondominios] = useState<number>(0);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);
    const [loadingInitial, setLoadingInitial] = useState<boolean>(false);
    const [hasMore, setHasMore] = useState<boolean>(false);
    const [draftFilters, setDraftFilters] = useState<CondominioFilters>(EMPTY_FILTERS);
    const [activeFilters, setActiveFilters] = useState<CondominioFilters>(EMPTY_FILTERS);
    const urlSyncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const allCondominiosRef = useRef<any[]>([]);

    useEffect(() => {
        allCondominiosRef.current = allCondominios;
    }, [allCondominios]);

    const hasDraftFilters = hasAnyFilter(draftFilters);
    const totalResultados = totalCondominios > 0 ? totalCondominios : allCondominios.length;
    const canonicalUrl = buildCanonical(init_props?.prodOrigin || site?.site, router.asPath || '/condominios');

    const allTypesMap = useMemo(() => {
        const types: Record<string, string> = {};
        allDataHome?.tipos?.forEach((tipo: any) => {
            if (tipo?.id != null && tipo?.nome) {
                types[String(tipo.id)] = tipo.nome;
            }
        });
        return types;
    }, [allDataHome?.tipos]);

    const updateFilter = useCallback((field: keyof CondominioFilters, value: string) => {
        setDraftFilters((prev) => ({
            ...prev,
            [field]: value
        }));
    }, []);

    const removeFilter = useCallback((field: keyof CondominioFilters) => {
        setDraftFilters((prev) => ({
            ...prev,
            [field]: ''
        }));
    }, []);

    const clearFilters = useCallback(() => {
        setDraftFilters(EMPTY_FILTERS);
    }, []);

    const mergeUniqueEmpreendimentos = useCallback((items: any[]) => {
        const map = new Map<number, any>();
        items.forEach((item: any) => {
            if (item?.id != null && !map.has(item.id)) {
                map.set(item.id, item);
            }
        });
        return Array.from(map.values());
    }, []);

    const loadEmpreendimentos = useCallback(async (page: number, append: boolean, filters: CondominioFilters) => {
        if (append) {
            setLoadingMore(true);
        } else {
            setLoadingInitial(true);
        }

        try {
            const data: any = await getCompanySearch(
                page,
                QTD_PAGINA,
                filters.nome || undefined,
                filters.cidade || undefined,
                filters.bairro || undefined,
                toOptionalNumber(filters.preco_min),
                toOptionalNumber(filters.preco_max),
                toOptionalNumber(filters.tipo),
                origin,
                codsite
            );

            const response = data?.empreendimentos_busca;
            const empreendimentos = response?.empreendimentos || [];
            const count = Number(response?.count || 0);
            const hasMoreByCount = count > (page * QTD_PAGINA);
            const hasMoreByPageSize = Array.isArray(empreendimentos) && empreendimentos.length >= QTD_PAGINA;

            if (response && Array.isArray(empreendimentos)) {
                const previousList = append ? allCondominiosRef.current : [];
                const nextList = append
                    ? mergeUniqueEmpreendimentos([...previousList, ...empreendimentos])
                    : empreendimentos;
                const hasMoreFromServer = hasMoreByCount || hasMoreByPageSize;
                const shouldKeepServerCount = nextList.length > 0 || hasMoreFromServer;

                setAllCondominios(nextList);
                setTotalCondominios(
                    shouldKeepServerCount && count > 0
                            ? count
                            : nextList.length
                );
                setHasMore(hasMoreFromServer);
                setPaginaAtual(page);
                return;
            }

            if (!append && !hasAnyFilter(filters)) {
                const fallback = await getCompany('', origin, codsite, true);
                const legacyEmpreendimentos = fallback?.empreendimentos || [];
                setAllCondominios(legacyEmpreendimentos);
                setTotalCondominios(legacyEmpreendimentos.length);
                setHasMore(false);
                setPaginaAtual(1);
                return;
            }

            if (!append) {
                setAllCondominios([]);
                setTotalCondominios(0);
                setHasMore(false);
                setPaginaAtual(1);
            }
        } catch (error) {
            if (!append) {
                setAllCondominios([]);
                setTotalCondominios(0);
            }
            setHasMore(false);
        } finally {
            if (append) {
                setLoadingMore(false);
            } else {
                setLoadingInitial(false);
            }
        }
    }, [codsite, mergeUniqueEmpreendimentos, origin]);

    const handleLoadMore = useCallback(() => {
        if (loadingInitial || loadingMore || !hasMore) {
            return;
        }
        loadEmpreendimentos(paginaAtual + 1, true, activeFilters);
    }, [activeFilters, hasMore, loadEmpreendimentos, loadingInitial, loadingMore, paginaAtual]);

    useEffect(() => {
        if (!router.isReady) {
            return;
        }

        const filtersFromUrl = normalizeFiltersFromQuery(router.query);
        const serialized = JSON.stringify(filtersFromUrl);

        setDraftFilters((prev) => JSON.stringify(prev) === serialized ? prev : filtersFromUrl);
        setActiveFilters((prev) => JSON.stringify(prev) === serialized ? prev : filtersFromUrl);
    }, [router.isReady, router.asPath, router.query]);

    useEffect(() => {
        if (!router.isReady) {
            return;
        }

        if (urlSyncTimeoutRef.current) {
            clearTimeout(urlSyncTimeoutRef.current);
        }

        urlSyncTimeoutRef.current = setTimeout(() => {
            const nextQuery = filtersToQuery(draftFilters);
            const currentQuery = filtersToQuery(normalizeFiltersFromQuery(router.query));

            if (JSON.stringify(nextQuery) !== JSON.stringify(currentQuery)) {
                const nextSearch = new URLSearchParams(nextQuery).toString();
                const nextHref = appendCodsiteQuery(
                    nextSearch ? `/condominios?${nextSearch}` : '/condominios',
                    codsite,
                    init_props?.prodOrigin || site?.site
                );
                router.replace(
                    nextHref,
                    undefined,
                    { shallow: true, scroll: false }
                );
            }
        }, 420);

        return () => {
            if (urlSyncTimeoutRef.current) {
                clearTimeout(urlSyncTimeoutRef.current);
            }
        };
    }, [codsite, draftFilters, init_props?.prodOrigin, router, router.isReady, router.asPath, router.query, site?.site]);

    useEffect(() => {
        if (!router.isReady) {
            return;
        }
        setAllCondominios([]);
        setPaginaAtual(0);
        setTotalCondominios(0);
        setHasMore(false);
        loadEmpreendimentos(1, false, activeFilters);
    }, [activeFilters, loadEmpreendimentos, router.isReady]);

    useEffect(() => {
        if (!isMobileFilter && activeLargeFilter) {
            setActiveLargeFilter(false);
        }
    }, [activeLargeFilter, isMobileFilter]);

    const activeFilterChips = useMemo(() => {
        const chips: Array<{ key: keyof CondominioFilters; label: string }> = [];

        if (activeFilters.nome) chips.push({ key: 'nome', label: `Nome: ${activeFilters.nome}` });
        if (activeFilters.cidade) chips.push({ key: 'cidade', label: `Cidade: ${activeFilters.cidade}` });
        if (activeFilters.bairro) chips.push({ key: 'bairro', label: `Bairro: ${activeFilters.bairro}` });
        if (activeFilters.tipo) {
            chips.push({
                key: 'tipo',
                label: `Tipo: ${allTypesMap[activeFilters.tipo] || activeFilters.tipo}`
            });
        }
        if (activeFilters.preco_min) chips.push({ key: 'preco_min', label: `Preco min: ${formatFilterCurrency(activeFilters.preco_min)}` });
        if (activeFilters.preco_max) chips.push({ key: 'preco_max', label: `Preco max: ${formatFilterCurrency(activeFilters.preco_max)}` });

        return chips;
    }, [activeFilters, allTypesMap]);

    const renderFiltersPanel = () => (
        <div className={styles.filtersPanel}>
            <div className={styles.filtersPanelHeader}>
                <div>
                    <h2 className={styles.filtersTitle}>Filtros</h2>
                    <p className={styles.filtersSubtitle}>Refine sua busca de empreendimentos</p>
                </div>
                {isMobileFilter ? (
                    <button
                        type="button"
                        className={styles.closeMobileFilter}
                        onClick={() => setActiveLargeFilter(false)}
                    >
                        <IoCloseSharp size={22} />
                    </button>
                ) : null}
            </div>

            <div className={styles.filtersGrid}>
                <label className={styles.filterField}>
                    <span>Nome</span>
                    <input
                        type="text"
                        value={draftFilters.nome}
                        onChange={(e) => updateFilter('nome', e.target.value)}
                        placeholder="Ex: Jardins"
                    />
                </label>

                <label className={styles.filterField}>
                    <span>Cidade</span>
                    <select
                        value={draftFilters.cidade}
                        onChange={(e) => updateFilter('cidade', e.target.value)}
                    >
                        <option value="">Todas</option>
                        {(allCity || []).map((cidade: any) => (
                            <option key={cidade?.id} value={cidade?.nome || ''}>
                                {cidade?.nome}
                            </option>
                        ))}
                    </select>
                </label>

                <label className={styles.filterField}>
                    <span>Bairro</span>
                    <input
                        type="text"
                        value={draftFilters.bairro}
                        onChange={(e) => updateFilter('bairro', e.target.value)}
                        placeholder="Ex: Centro"
                    />
                </label>

                <label className={styles.filterField}>
                    <span>Tipo</span>
                    <select
                        value={draftFilters.tipo}
                        onChange={(e) => updateFilter('tipo', e.target.value)}
                    >
                        <option value="">Todos</option>
                        {(allDataHome?.tipos || []).map((tipo: any) => (
                            <option key={tipo?.id} value={String(tipo?.id || '')}>
                                {tipo?.nome}
                            </option>
                        ))}
                    </select>
                </label>

                <label className={styles.filterField}>
                    <span>Preco minimo</span>
                    <input
                        type="text"
                        inputMode="numeric"
                        value={draftFilters.preco_min}
                        onChange={(e) => updateFilter('preco_min', e.target.value.replace(/[^\d]/g, ''))}
                        placeholder="Ex: 350000"
                    />
                </label>

                <label className={styles.filterField}>
                    <span>Preco maximo</span>
                    <input
                        type="text"
                        inputMode="numeric"
                        value={draftFilters.preco_max}
                        onChange={(e) => updateFilter('preco_max', e.target.value.replace(/[^\d]/g, ''))}
                        placeholder="Ex: 900000"
                    />
                </label>
            </div>

            <div className={styles.actionsRow}>
                <button
                    type="button"
                    className={styles.clearButton}
                    onClick={clearFilters}
                    disabled={!hasDraftFilters}
                >
                    Limpar filtros
                </button>
                {isMobileFilter ? (
                    <button
                        type="button"
                        className={styles.applyMobileButton}
                        onClick={() => setActiveLargeFilter(false)}
                    >
                        Concluido
                    </button>
                ) : null}
            </div>
        </div>
    );

    const renderRodape = () => {
        switch (site?.rodape) {
            case 1:
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                        viewport={{ once: true, amount: 0.22 }}
                    >
                        <AboutPieces1 site={site} allCities={allCities} allCitiesTipes={allDataHome?.tipos} codsite={codsite2} origin={origin} isMobile={isMobile1} allRodape={allDataHome?.rodape} favorito={favorito}/>
                    </motion.div>
                );
            case 2:
                return (
                    <AboutPieces allRodape={allDataHome?.rodape} site={site} allCities={allCities} allCitiesTipes={allDataHome?.tipos} codsite={codsite2} origin={origin} isMobile={isMobile1}/>
                );
            case 3:
                return (
                    <AboutPieces2 site={site} allRodape={allDataHome?.rodape}/>
                );
            default:
            return (
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    viewport={{ once: true, amount: 0.22 }}
                >
                    <AboutPieces1 site={site} allCities={allCities} allCitiesTipes={allDataHome?.tipos} codsite={codsite2} origin={origin} isMobile={isMobile1} allRodape={allDataHome?.rodape} favorito={favorito}/>
                </motion.div>
            );
        }
    };

    return (
        <div className={styles.container}>
            <Head>
                <title>{`Empreendimentos | ${site?.titulo || site?.Dados?.empresa || 'Imobiliaria'}`}</title>
                <meta name="robots" content="index, follow" />
                <meta property="og:url" content={canonicalUrl} />
                <link rel="canonical" href={canonicalUrl} key="canonical" />
            </Head>
            <PopUpComponent />
            <WhatsAppComponent site={site} />
            <WelcomePiecesProperties2
                site={site}
                favorito={favorito}
                props={allDataHome}
            />

            {isMobileFilter && activeLargeFilter ? (
                <div className={styles.containerLargeFilter}>
                    {renderFiltersPanel()}
                </div>
            ) : null}

            <section className={styles.pageBody}>
                {!isMobileFilter ? (
                    <aside className={styles.sidebar}>
                        {renderFiltersPanel()}
                    </aside>
                ) : null}

                <div className={styles.resultsColumn}>
                    <div className={styles.resultsHeader}>
                        <div>
                            <h2 className={styles.resultsTitle}>Empreendimentos</h2>
                            <p className={styles.resultsCount}>
                                {loadingInitial ? 'Buscando...' : `${totalResultados} resultados`}
                            </p>
                        </div>
                        {isMobileFilter ? (
                            <button
                                type="button"
                                className={styles.openFilterButton}
                                onClick={() => setActiveLargeFilter(true)}
                            >
                                <IoFilterOutline size={20} />
                                Filtrar
                            </button>
                        ) : null}
                    </div>

                    {activeFilterChips.length > 0 ? (
                        <div className={styles.chipsRow}>
                            {activeFilterChips.map((chip) => (
                                <button
                                    key={chip.key}
                                    type="button"
                                    className={styles.filterChip}
                                    onClick={() => removeFilter(chip.key)}
                                >
                                    <span>{chip.label}</span>
                                    <IoCloseSharp size={16} />
                                </button>
                            ))}
                            <button
                                type="button"
                                className={styles.clearAllChips}
                                onClick={clearFilters}
                            >
                                Limpar todos
                            </button>
                        </div>
                    ) : null}

                    <ListPiecesCondominio
                        allCompany={allCondominios}
                        site={site}
                        allCities={allCity}
                        allTypesMap={allTypesMap}
                        origin={origin}
                        codsite={codsite}
                        onLoadMore={handleLoadMore}
                        hasMore={hasMore}
                        loadingMore={loadingMore}
                        loadingInitial={loadingInitial}
                    />
                </div>
            </section>
            { renderRodape() }
        </div>
    );
};

export default Company;
